import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback, useRef } from "react";
import {
  Card,
  Stack,
  Container,
  Typography,
  CircularProgress,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import Scrollbar from "../components/scrollbar";
import { useSearchParams } from "react-router-dom";
import CardsOnSaleTable from "src/components/cards-table/CardsOnSaleTable";
import ActivityTable from "src/components/cards-table/ActivityTable";
import { get } from "src/utils/networkUtils";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 1, paddingTop: 3 }}>{children}</Box>}
    </div>
  );
}
export default function UserMarketplaceCardsPage() {
  const [loading, setLoading] = useState(true);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [cards, setCards] = useState([]);
  const [activity, setActivity] = useState([]);
  const [searchParams] = useSearchParams();
  const [value, setValue] = useState(0);
  const [offset, setOffset] = useState(0);
  const [activityOffset, setActivityOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [hasMoreActivity, setHasMoreActivity] = useState(true);
  const observer = useRef();
  const activityObserver = useRef();

  const LIMIT = 50;

  const lastCardRef = useCallback(
    (node) => {
      if (cardsLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setOffset((prev) => prev + LIMIT);
        }
      });
      if (node) observer.current.observe(node);
    },
    [cardsLoading, hasMore]
  );

  const lastActivityRef = useCallback(
    (node) => {
      if (activityLoading) return;
      if (activityObserver.current) activityObserver.current.disconnect();
      activityObserver.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMoreActivity) {
          setActivityOffset((prev) => prev + LIMIT);
        }
      });
      if (node) activityObserver.current.observe(node);
    },
    [activityLoading, hasMoreActivity]
  );

  useEffect(() => {
    if (!userId) return;

    if (value === 0) {
      setCardsLoading(true);
      if (offset === 0) {
        setCards([]);
        setLoading(true);
      }
      get(
        `/admin/marketplace/user-cards/${userId}?offset=${offset}&limit=${LIMIT}`
      ).then((response) => {
        setCards((prev) => [...prev, ...response.cards]);
        setHasMore(response.cards.length === LIMIT);
        setCardsLoading(false);
        if (offset === 0) setLoading(false);
      });
    } else if (value === 1) {
      setActivityLoading(true);
      if (activityOffset === 0) {
        setActivity([]);
        setLoading(true);
      }
      get(
        `/admin/marketplace/user-activity/${userId}?offset=${activityOffset}&limit=${LIMIT}`
      ).then((response) => {
        setActivity((prev) => [...prev, ...response.activity]);
        setHasMoreActivity(response.activity.length === LIMIT);
        setActivityLoading(false);
        if (activityOffset === 0) setLoading(false);
      });
    }
  }, [value, offset, activityOffset, userId]);

  useEffect(() => {
    if (searchParams.has("user_id")) {
      const id = searchParams.get("user_id");
      setUserId(id);
    }
  }, [searchParams]);

  const handleChangeTabs = (event, newValue) => {
    setValue(newValue);
    // Reset pagination when switching tabs
    setOffset(0);
    setActivityOffset(0);
    // Clear existing data
    setCards([]);
    setActivity([]);
    // Reset hasMore flags
    setHasMore(true);
    setHasMoreActivity(true);
  };

  return (
    <>
      <Helmet>
        <title> User Marketplace Cards </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            User Marketplace Cards
          </Typography>
        </Stack>

        <Card sx={{ p: 1 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={value}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
            >
              <Tab label="Cards On Sale" {...a11yProps(0)} />
              <Tab label="Marketplace Activity" {...a11yProps(1)} />
            </Tabs>
          </Box>
          <TabPanel value={value} index={0}>
            {cards === null || loading ? (
              <Box
                sx={{ display: "flex", justifyContent: "center" }}
                style={{ padding: 20 }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Scrollbar>
                <CardsOnSaleTable
                  cards={cards}
                  userId={userId}
                  lastCardRef={lastCardRef}
                />
                {cardsLoading && (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress />
                  </Box>
                )}
              </Scrollbar>
            )}
          </TabPanel>

          <TabPanel value={value} index={1}>
            {activity === null || loading ? (
              <Box
                sx={{ display: "flex", justifyContent: "center" }}
                style={{ padding: 20 }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Scrollbar>
                <ActivityTable
                  activity={activity}
                  lastActivityRef={lastActivityRef}
                />
                {activityLoading && (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress />
                  </Box>
                )}
              </Scrollbar>
            )}
          </TabPanel>
        </Card>
      </Container>
    </>
  );
}
